import { makeStyles, Theme } from '@material-ui/core/styles'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import AppBreadcrumbs from 'components/Headers/AppBreadcrumbs'
import { RouteDictionary } from 'components/Models/routeTypes'
import ContainerMaxWidthXl from 'components/Surfaces/ContainerMaxWidthXl'
import GridContainer from 'components/Surfaces/GridContainer'
import GridItem from 'components/Surfaces/GridItem'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    lowerBar: (props: LowerToolbarStyleProps) => ({
        minHeight: props.lowerBarHeight,
        backgroundColor: props.lowerBarBackgroundColor,
        height: props.fullHeight ? '100%' : 'unset',
    }),
    lowerBarTop: {
        boxShadow:
            '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
        '& .MuiGrid-item': {
            paddingRight: theme.spacing(2),
        },
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        scrollbarWidth: 'none',
    },
    lowerBarBottom: {
        borderBottom: '1px solid #C9C9C9',
    },
    lowerBarItem: (props: LowerToolbarStyleProps) => ({
        height: props.fullHeight ? '100%' : 'unset',
        paddingRight: 0,
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        scrollbarWidth: 'none',
    }),
    lowerBarItemContainer: {
        '&>div': {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
    },
    padded: {
        padding: theme.spacing(0.25),
        paddingRight: 0,
    },
    fullWidth: {
        width: '100%',
    },
    customText: {
        whiteSpace: 'nowrap',
        color: (props: LowerToolbarStyleProps) =>
            props.customTextColor ?? '#04325B',
    },
}))

export interface LowerToolbarStyleProps {
    /** Lower bar's background color. */
    lowerBarBackgroundColor: string
    /** Lower bar's height. */
    lowerBarHeight: number
    /** Has full height of container */
    fullHeight?: boolean

    customTextColor?: string
}

export interface LowerToolbarProps extends Partial<LowerToolbarStyleProps> {
    /** Components on the right side of the toolbar*/
    customComponent?: React.ReactNode
    /** Route for the breadcrumbs*/
    routes: RouteDictionary
    /** Set to true if lowerbottom is showns*/
    setHasLowerBottomToolbar: (value: boolean) => void
    /** Lowerbottom check*/
    hasLowerBottomToolbar?: boolean
    customText?: string
    customTextColor?: string
    isCustomToolbar?: boolean
}

const LowerToolbar: React.FC<LowerToolbarProps> = ({
    lowerBarBackgroundColor = '#fff',
    lowerBarHeight = 50,
    customComponent,
    customText,
    customTextColor,
    routes,
    setHasLowerBottomToolbar,
    hasLowerBottomToolbar,
    fullHeight = false,
    isCustomToolbar = false,
}) => {
    const classes = useStyles({
        lowerBarBackgroundColor,
        lowerBarHeight,
        fullHeight,
        customTextColor,
    })
    const limit = 700 //window width limit

    const textContentRef = React.useRef<HTMLDivElement | null>(null)
    const customComponentRef = React.useRef<HTMLDivElement | null>(null)

    const check = () => {
        if (
            customComponent &&
            customComponentRef?.current &&
            textContentRef?.current &&
            window.innerWidth < limit &&
            customComponentRef.current.children.length > 0
        ) {
            const bRect = textContentRef.current.getBoundingClientRect()
            const cRect = customComponentRef.current.getBoundingClientRect()
            setHasLowerBottomToolbar(cRect.x <= bRect.x)
        } else {
            setHasLowerBottomToolbar(false)
        }
    }

    const onEvent = React.useCallback(() => {
        const interval = setTimeout(check, 200)
        return () => {
            clearInterval(interval)
        }
    }, [customComponent, textContentRef, setHasLowerBottomToolbar])

    React.useEffect(check, [
        customComponent,
        textContentRef,
        setHasLowerBottomToolbar,
        fullHeight,
    ])

    React.useEffect(() => {
        window.addEventListener('resize', onEvent)
        return () => {
            window.removeEventListener('resize', onEvent)
        }
    }, [onEvent])

    return (
        <>
            {!isCustomToolbar ? (
                <>
                    {!hasLowerBottomToolbar && (
                        <Toolbar
                            className={clsx(
                                'doubleAppToolBarLower',
                                classes.lowerBar
                            )}
                        >
                            <ContainerMaxWidthXl>
                                <GridContainer
                                    className={classes.lowerBarItem}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <GridItem ref={textContentRef}>
                                        {customText ? (
                                            <Typography
                                                variant="h1"
                                                className={classes.customText}
                                            >
                                                {customText}
                                            </Typography>
                                        ) : (
                                            <AppBreadcrumbs routes={routes} />
                                        )}
                                    </GridItem>
                                    {customComponent && (
                                        <GridItem
                                            ref={customComponentRef}
                                            className={clsx(
                                                classes.lowerBarItem,
                                                classes.padded
                                            )}
                                        >
                                            {customComponent}
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </ContainerMaxWidthXl>
                        </Toolbar>
                    )}
                    {hasLowerBottomToolbar && (
                        <>
                            <Toolbar
                                className={clsx(
                                    'doubleAppToolBarLowerTop',
                                    classes.lowerBar,
                                    classes.lowerBarTop
                                )}
                            >
                                <ContainerMaxWidthXl>
                                    <GridContainer
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <GridItem ref={textContentRef}>
                                            {customText ? (
                                                <Typography
                                                    variant="h1"
                                                    className={
                                                        classes.customText
                                                    }
                                                >
                                                    {customText}
                                                </Typography>
                                            ) : (
                                                <AppBreadcrumbs
                                                    routes={routes}
                                                />
                                            )}
                                        </GridItem>
                                    </GridContainer>
                                </ContainerMaxWidthXl>
                            </Toolbar>

                            <ContainerMaxWidthXl>
                                <GridContainer
                                    className={clsx(
                                        'doubleAppToolBarLowerBottom',
                                        classes.lowerBarBottom,
                                        classes.lowerBar
                                    )}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    {customComponent && (
                                        <GridItem
                                            className={clsx(
                                                classes.lowerBarItem,
                                                classes.lowerBarItemContainer,
                                                classes.padded
                                            )}
                                            ref={customComponentRef}
                                        >
                                            {customComponent}
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </ContainerMaxWidthXl>
                        </>
                    )}
                </>
            ) : (
                <Toolbar className={classes.lowerBar}>
                    <ContainerMaxWidthXl>
                        <GridContainer item className={classes.lowerBarItem}>
                            {customComponent}
                        </GridContainer>
                    </ContainerMaxWidthXl>
                </Toolbar>
            )}
        </>
    )
}

export default LowerToolbar
